import type {
  CLIENT_TYPE,
  FinanceProvider,
  FinanceResponseStatus,
  FinanceApplicationStatus,
  FinanceType,
  Condition,
} from './constants';
import type { Car, CarId } from './carTypes';
import type { Deal, DealId, TradeIn } from './dealTypes';
import type { Dealer, DealerFee, TaxBreakdown } from './dealerTypes';
import { User } from './userTypes';
import type { PaymentData as Data } from './paymentTypes';
import { SelectedAccessory } from './accessoryTypes';
import type { Bundle, ProductCategory, SelectedProduct } from './penTypes';
import type { AddOn } from './pencilTypes';
import type { ValidateResponseResult } from '../response-utils';

export type PaymentDataId = string;

export interface PaymentData {
  id: PaymentDataId;
  data: Data;
}

export interface OptionalProductAccessoryData {
  selectedAccessories?: SelectedAccessory[];
  selectedAddOns?: AddOn[];
  selectedBundles?: Bundle[];
  selectedProducts?: SelectedProduct[];
  protectionPlanProductPriceOverrides?: { [key: string]: number };
}

export interface ApplicationData extends OptionalProductAccessoryData {
  financeType: FinanceType;
  downPayment: number;
  months: number;
  rebate: number;
  apr: number;
  fees: number;
  // This additional fees is sum of all fees except doc fees
  additionalFees?: number;
  docFee?: number;
  taxAmount: number;
  vehiclePrice: number;
  financeAmount: number;
  msrp: number;
  invoice: number;
  bookValue: number;
  discount: number;
  effectivePrice: number;
  upgrades: number;
  monthly: number;
  tradePayoff: number;
  tradeInAllowance: number;
  tradeIns?: TradeIn[];
  netTradeAmount?: number;
  securityDeposit?: number;
  monthlyTax?: number;
  estimatedAnnualMileage?: number;
  residualAmount?: number;
  residualPercentage?: number;
  capitalizedCost?: number;
  baseMonthlyPayment?: number;
  leaseRateMoneyFactor?: number;
  price?: number;
  grossCapitalizedCost?: number;
  selectedLenderIds?: string[];
}
export interface FinanceApplicationPayload {
  car?: Car;
  dealer: Dealer;
  deal: Deal;
  user: User;
  clientType: CLIENT_TYPE;
  applicationData?: ApplicationData;
  paymentData?: PaymentData;

  // This flag is true when we submit a credit application in the lending flow.
  submitToLenders?: boolean;

  // Westlake to RouteOne identifier
  dcAppNo?: string;

  // Flag to allow/prevent submitting a credit application to Upstart.
  sendToUpstart?: boolean;

  // Flag to determine if credit first lending flow should occur (Upstart)
  creditFirstLending?: boolean;

  // Flag to determine if submitted application comes from Digital Finance.
  // if this is on, application will always submit to upstart.
  digitalFinance?: boolean;

  // Flag to skip invalidating existing offers for manual F&I lender submission
  skipActiveOfferInvalidation?: boolean;

  // A list of finance source IDs for Submit to Select Lenders functionality
  selectedLenderIds?: string[];
}

export type FinanceApplicationPayloadWithCar = FinanceApplicationPayload & {
  car: Car;
};

export type FinanceApplicationId = string;

export type FinanceResponseId = string;

export interface AttachedFile {
  id?: string;
  fileName?: string;
  fileExtension?: string;
  contentType?: string;
  uploadedAt?: Date | string;
  uploadedByUserId?: number | null;
  stipulation?: string;
  url?: string | null;
  src?: string | Blob | null;
}

export interface FinanceApplicationResponse {
  status?: string;
  errors?: string[];
  pricingRequestUuid?: string;
}

export interface FinanceApplication {
  id: FinanceApplicationId;
  dealId?: DealId;
  userId?: number;
  carId?: CarId;
  provider?: FinanceProvider;
  providerApplicationId?: string;
  paymentDataId?: PaymentDataId;
  clientType?: CLIENT_TYPE;
  data?: ApplicationData;
  status?: FinanceApplicationStatus;
  attachedFiles?: AttachedFile[];
  verificationEmail?: string;
  response?: FinanceApplicationResponse;
  createdAt?: string | Date;
  updatedAt?: string | Date;
  expiredAt?: string | Date;
  creditFirstSubmission?: boolean;
  funded?: boolean;
  isFindingOffersRequested?: boolean;
  isDeskingUpdated?: boolean;
  responses?: FinanceResponse[];
  creditBureau?: string[];
  ipAddress?: string;
  customerCreditPullPiiDigest?: string | null;
}

export interface ReportFinanceApplication extends FinanceApplication {
  financeResponses?: FinanceResponse[];
  user?: Pick<User, 'firstName' | 'lastName'>;
  dealer?: Pick<Dealer, 'id' | 'name'>;
  deal: Pick<Deal, 'applicant' | 'selectedFinanceResponseId'>;
  car: Pick<Car, 'stock' | 'condition' | 'name' | 'id'>;
}

export enum FinalFeeType {
  ADMINISTRATIVE_FEE = 'administrativeFee',
  CALIFORNIA_TIRE = 'californiaTire',
  CASH_LIEN_NOTATION = 'cashLienNotation',
  DEALER_INVENTORY_TAX = 'dealerInventoryTax',
  DELIVERY_CASH = 'deliveryCash',
  DEPUTY_SERVICE = 'deputyService',
  DOCUMENT_PROCESSING = 'documentProcessing',
  DOCUMENTARY_STAMP_TAX = 'documentaryStampTax',
  DMV_APPS = 'dmvApps',
  ELECTRONIC_LIEN_TITLE = 'electronicLienTitle',
  ELECTRONIC_REGISTRATION = 'electronicRegistration',
  EMISSIONS_CERTIFICATION = 'emissionsCertification',
  EMISSIONS_EXEMPTION = 'emissionsExemption',
  EMISSIONS_TESTING = 'emissionsTesting',
  EXCISE_TAX = 'exciseTax',
  FINANCED_LIEN_NOTATION = 'financedLienNotation',
  FREIGHT_CASH = 'freightCash',
  GOVERNMENT_FEES_TAX = 'governmentFeesTax',
  GOVERNMENT_REGISTRATION = 'governmentRegistration',
  INFRASTRUCTURE_MAINTENANCE = 'infrastructureMaintenance',
  INSPECTION_OTHER = 'inspectionOther',
  INSPECTION_TO_STATION = 'inspectionToStation',
  PLATE_TRANSFER = 'plateTransfer',
  REGISTRATION = 'registration',
  REGISTRATION_CONVENIENCE = 'registrationConvenience',
  TOTAL_GOVERNMENT_CERTIFICATE_TITLE = 'totalGovernmentCertificateTitle',
  SUPPLEMENTAL_TITLE = 'supplementalTitle',
  VEHICLE_DELIVERY = 'vehicleDelivery',
  VEHICLE_LICENSE = 'vehicleLicense',
  OTHER = 'other',
}

export interface FinalFee {
  type: FinalFeeType;
  value: number;
  description?: string;
  paidTo?: string;
}

// Used by DAPI to categorize products for backend calculations.
export enum FinalProductOrigin {
  FEE = 'fee',
  PRODUCT = 'product',
  ACCESSORY = 'accessory',
  TAX = 'tax',
  OTHER = 'other',
}

export interface FinalProduct {
  value?: number | null;
  category?: ProductCategory | null;
  description?: string | null;
  paidTo?: string | null;
  miles?: number | null;
  months?: number | null;
  origin?: FinalProductOrigin;
}

export interface OtherCharge {
  description?: string;
  paidTo?: string;
  amount?: number;
}

export enum InsuranceType {
  DOUBLE_INTEREST = 'doubleInterest',
  CREDIT_DISABILITY = 'creditDisability',
  CREDIT_LIFE = 'creditLife',
  DEBT_CANCELLATION = 'debtCancellation',
  FULL_COVERAGE = 'fullCoverage',
  GAP = 'gap',
  INVOLUNTARY_UNEMPLOYMENT = 'involuntaryUnemployment',
  LIABILTY_COVERAGE = 'liabilityCoverage',
  MECHANICAL_BREAKDOWN = 'mechanicalBreakdown',
  OTHER = 'other',
  PUBLIC_LIABILITY = 'publicLiability',
  SINGLE_INTEREST = 'singleInterest',
}

export const InsuranceTypeLabelMap = {
  [InsuranceType.DOUBLE_INTEREST]: 'Double Interest',
  [InsuranceType.CREDIT_DISABILITY]: 'Credit Disability',
  [InsuranceType.CREDIT_LIFE]: 'Credit Life',
  [InsuranceType.DEBT_CANCELLATION]: 'Debt Cancellation',
  [InsuranceType.FULL_COVERAGE]: 'Full Coverage',
  [InsuranceType.GAP]: 'Gap',
  [InsuranceType.INVOLUNTARY_UNEMPLOYMENT]: 'Involuntary Unemployment',
  [InsuranceType.LIABILTY_COVERAGE]: 'Liability Coverage',
  [InsuranceType.MECHANICAL_BREAKDOWN]: 'Mechanical Breakdown',
  [InsuranceType.OTHER]: 'Other',
  [InsuranceType.PUBLIC_LIABILITY]: 'Public Liability',
  [InsuranceType.SINGLE_INTEREST]: 'Single Interest',
};

export interface InsuranceAddress {
  lineOne: string;
  lineTwo?: string | null;
  city: string;
  stateCode: string;
  zip: string;
}

export interface Insurance {
  type: InsuranceType;
  insuranceCompanyName: string;
  premium: number;
  address?: InsuranceAddress;
  termMonths?: number;
  contractTermMileage?: number;
}

export interface UpstartRateBadge {
  value: string;
}

// extra input keys to be used when displaying RouteOne rates
export interface DecisionResponseVehicleData {
  sellingPrice?: number;
  msrpPrice?: number;
  wholesalePrice?: number;
  mileage?: number;
}

export interface FinanceResponseData
  extends OptionalProductAccessoryData,
    DecisionResponseVehicleData {
  error?: string;
  selected?: boolean;
  fees?: number;
  taxAmount?: number;
  months?: number;
  rebate?: number;
  monthly?: number;
  markup?: number;
  markupBps?: number;
  effectivePrice?: number;
  maximumMarkup?: number;
  financeAmount?: number;
  taxPercent?: number;
  minFinanceAmount?: number;
  maxFinanceAmount?: number;
  minDownPaymentAmount?: number;
  maxDownPaymentAmount?: number;
  dealerReserve?: number;
  dealerFeePercentage?: number;
  downPayment?: number;
  netTrade?: number;
  displayPrice?: number;
  apr?: number;
  sellRate?: number;
  buyRate?: number;
  discount?: number;
  incentive?: number;
  stipulations?: string[];
  rates?: Rate[];
  receivedAt?: Date | string;
  taxBreakdown?: TaxBreakdown;
  feeList?: DealerFee[];
  tradeIns?: TradeIn[];
  contractMonths?: number;
  firstPaymentDueDate?: string;
  financeType?: FinanceType;
  effectiveFinanceAmount?: number;
  analystName?: string;
  analystContactInfo?: string;
  car?: Car;
  tier?: string | number;
  programName?: string;
  customerRate?: number;
  miscNotes?: string;
  maxPaymentToIncome?: number;
  participation?: number;
  flat?: number;
  actualLTV?: number;
  maximumLTV?: number;
  purchasePrice?: number;
  docFees?: number;
  otherFees?: number;
  expiresAt?: string;
  comments?: string[];
  backendCalculationVersion?: number;
  assetValue?: number | null;
  primaryValueBadge?: UpstartRateBadge;
  upstartRateId?: string; // the associated upstartRateId if response offer comes an upstart rate. Not to be confused with rate.id PK
  // below are the fields specific to RIC Review page.
  accessoriesPrice?: number;
  aprOverride?: number;
  monthlyOverride?: number;
  finalizedFeeList?: FinalFee[];
  finalizedProductList?: FinalProduct[];
  taxAmountOverride?: number;
  carStock?: string;
  vehicleWeight?: number;
  contractNumber?: string;
  carBody?: string;
  carCondition?: Condition;
  carDefects?: OtherCharge[];
  deferredDownPayment?: number;
  rightToCancelDays?: number;
  insurances?: Insurance[];
  otherNonTaxableCharges?: OtherCharge[];
  otherTaxableCharges?: OtherCharge[];
  otherDownPaymentDescription?: string;
  otherDownPaymentAmount?: number;
  otherDownPayments?: OtherCharge[];
  mileage?: number;
  // Dealer compensation summary for an Upstart offer
  dealerCompensationSummary?: DealerCompensationSummary;
}

export interface DealerCompensationSummary {
  dealerFeeAmount: number;
  dealerFeePercentage: number;
  dealerFlatAmount: number;
  dealerFlatPercentage: number;
  dealerReserveAmount: number;
  dealerReserveMaxAmount: number;
  dealerCompensationAmount: number;
}

export interface FinanceResponse {
  id: FinanceResponseId;
  createdAt?: string;
  updatedAt?: string;
  financeSourceName?: string;
  status?: FinanceResponseStatus;
  applicationId?: FinanceApplicationId;
  data?: FinanceResponseData;
  raw?: string;
  financingReviewedAt?: Date;
  financingReviewedBy?: number;
  lockedAt?: Date;
  FinanceApplication?: FinanceApplication;
  calculatedBackendAmount?: number;
  financeResponseRateId?: string;
  currentRate?: Rate;
  validations?: ValidateResponseResult;
  selected?: boolean;
}

export interface FinanceResponseRate {
  id: string;
  rates: Rate[];
  createdAt: string;
  updatedAt: string;
}

export type ApplicationId = string;

export type RateId = string;

export enum RateStatus {
  DENIED = 'denied',
  CONDITION = 'condition',
}

// Rate represents the data that is returned from Upstart and stored in financeResponse
export interface Rate {
  rateId: RateId;
  status: RateStatus;
  months: number;
  apr: number;
  fees: number;
  minFinanceAmount?: number;
  maxFinanceAmount: number;
  minDownPaymentAmount?: number;
  maxDownPaymentAmount?: number;
  markupMaxBps?: number;
  stipulations: string[];
  dealerReserve: number;
  dealerFeePercentage?: number;
  financeSourceName: string;
  disqualificationReasons?: string[];
  requiresCreditUnionMembership?: boolean;
  maxBackendAmount?: number;
  isCounterOffer?: boolean;
}

// MinimalRate represents the minimum amount of data needed to list financial details for an offer
export type MinimalRate = Pick<
  Rate,
  | 'rateId'
  | 'months'
  | 'maxFinanceAmount'
  | 'minDownPaymentAmount'
  | 'maxDownPaymentAmount'
  | 'markupMaxBps'
  | 'requiresCreditUnionMembership'
  | 'maxBackendAmount'
> & {
  // APR can be nullable for rate gaps
  apr: number | null;
  // Min finance amount is non-nullable in the minimal rate object
  minFinanceAmount: number;
};

// Down payment rates will be consolidated for display and we want to be able
// to identify which rateIds will be included in the consolidated rate
export type ConsolidatedDownPaymentRate = Omit<MinimalRate, 'rateId'> & {
  rateIds: RateId[];
};

export interface EmailVerificationResponse {
  success: boolean;
  message?: string;
}

interface UpstartUploadDocumentInfo {
  code: string;
  title: string;
  description: string;
  status: string;
  hasApplicantActionNeeded: boolean;
  statusCode: string;
  rejectionReason: string | null;
  assetUrls?: string[] | null;
}

export interface UpstartApplicationStatus {
  applicationId?: string;
  isStatusFinal?: boolean;
  code?: ApplicationStatusCodes;
  title?: string;
  description?: string;
  notes?: string;
  hasEmailBeenVerified?: boolean;
  isEmailVerificationRequired?: boolean;
  hasEmailBeenConfirmed?: boolean;
  isEmailConfirmationRequired?: boolean;
  hasStipulationGenerationCompleted?: boolean;
  hasUpdateInProgress?: boolean;
  stipulations?: UpstartUploadDocumentInfo[];
  additionalUploadedDocuments?: UpstartUploadDocumentInfo[];
  signedOn?: string | null;
  voidedOn?: string | null;
  downloadUrls?: {
    type?: string;
    url?: string;
    document?: string;
  }[];
  signingUrl?: string | null;
  contractNumber?: string | null;
  adverseActionNoticeInfo?: {
    url: string;
    reasons: {
      string: string;
      symbol: string;
    }[];
  };
  canUpdateEmail?: boolean;
}

export enum ApplicationStatusCodes {
  Preliminary = 'PRELIM',
  Disqualified = 'DQ',
  ApprovedConditional = 'APPROVE-C',
  Approved = 'APPROVE',
  InReview = 'REVIEW',
  Rejected = 'REJECT',
  Cancelled = 'CANCEL',
  Booked = 'BOOKED',
  Funded = 'FUNDED',
}

// States that have access to upstart lending
export enum UpstartLendingStates {
  AL = 'AL',
  AZ = 'AZ',
  AR = 'AR',
  CA = 'CA',
  CO = 'CO',
  CT = 'CT',
  FL = 'FL',
  GA = 'GA',
  IA = 'IA',
  IL = 'IL',
  IN = 'IN',
  KY = 'KY',
  KS = 'KS',
  LA = 'LA',
  MA = 'MA',
  MI = 'MI',
  MD = 'MD',
  ME = 'ME',
  MN = 'MN',
  MO = 'MO',
  MS = 'MS',
  MT = 'MT',
  NC = 'NC',
  NH = 'NH',
  NJ = 'NJ',
  OH = 'OH',
  OR = 'OR',
  OK = 'OK',
  PA = 'PA',
  RI = 'RI',
  SC = 'SC',
  TN = 'TN',
  TX = 'TX',
  UT = 'UT',
  VA = 'VA',
  WA = 'WA',
  WI = 'WI',
  WV = 'WV',
  WY = 'WY',
}

/* List of community property states. Has implications for both DT and Upstart Credit applications. */
export const COMMUNITY_PROPERTY_STATES = ['WI'];

// below are types for the offer audit feature

export enum AuditLogType {
  PROPERTY_CHANGE = 'propertyChange',
  PROPERTY_LIST_CHANGE = 'propertyListChange',
}

export enum ListItemChangeType {
  EDIT = 'edit',
  ADD = 'add',
  DELETE = 'delete',
}

export type PropertyChange = {
  name: string;
  oldValue?: number | string | boolean | null;
  value?: number | string | boolean | null;
};

export type ListItemChange = PropertyChange & {
  type: ListItemChangeType;
};

export type PropertyChangeLog = PropertyChange & {
  type: AuditLogType.PROPERTY_CHANGE;
};

export type PropertyListChangeLog = {
  type: AuditLogType.PROPERTY_LIST_CHANGE;
  title: string;
  listItemChanges?: ListItemChange[];
  oldTotal?: number;
  newTotal?: number;
};

export type ChangeSet = {
  changeLogs: (PropertyChangeLog | PropertyListChangeLog)[];
  createdAt?: Date;
  createdBy?: string;
};

export type FinanceResponseAudit = {
  financeResponseId: string;
  logs?: ChangeSet[];
  createdAt?: Date;
  updatedAt?: Date;
};
